import {
  AppearanceStyle,
  ColorScheme,
} from '@tribeplatform/design-system/types'
import { TranslateFunctionType } from '@tribeplatform/react-components/i18n'
import type { NetworkColorScheme } from '@tribeplatform/react-ui-kit/ThemeProvider'

export const getTranslatedCornerStyles = (
  $t: TranslateFunctionType,
  style: AppearanceStyle['corners'],
) => {
  switch (style) {
    case 'round':
      return $t({ defaultMessage: 'Round', id: 'Admin.Appearance.Style.Round' })
    case 'sharp':
      return $t({
        defaultMessage: 'Sharp',
        id: 'Admin.Appearance.Style.Sharp',
      })
    default: {
      const exhaustiveCheck: never = style
      return exhaustiveCheck
    }
  }
}
export const getTranslatedCardStyles = (
  $t: TranslateFunctionType,
  style: AppearanceStyle['cards'],
) => {
  switch (style) {
    case 'elevated':
      return $t({
        defaultMessage: 'Elevated',
        id: 'Admin.Appearance.Style.Elevated',
      })
    case 'bordered':
      return $t({
        defaultMessage: 'Bordered',
        id: 'Admin.Appearance.Style.Bordered',
      })
    case 'plain':
      return $t({
        defaultMessage: 'Plain',
        id: 'Admin.Appearance.Style.Plain',
      })
    default: {
      const exhaustiveCheck: never = style
      return exhaustiveCheck
    }
  }
}

export const getTranslatedButtonStyles = (
  $t: TranslateFunctionType,
  style: AppearanceStyle['buttons'],
) => {
  switch (style) {
    case 'pill':
      return $t({
        defaultMessage: 'Pill',
        id: 'Admin.Appearance.Style.Pill',
      })
    case 'rounded':
      return $t({
        defaultMessage: 'Rounded',
        id: 'Admin.Appearance.Style.Rounded',
      })
    case 'sharp':
      return $t({
        defaultMessage: 'Sharp',
        id: 'Admin.Appearance.Style.Sharp',
      })
    default: {
      const exhaustiveCheck: never = style
      return exhaustiveCheck
    }
  }
}

export const getTranslatedAvatarShapeStyles = (
  $t: TranslateFunctionType,
  style: AppearanceStyle['avatarShape'],
) => {
  switch (style) {
    case 'full-rounded':
      return $t({
        defaultMessage: 'Circle',
        id: 'Admin.Appearance.Style.Circle',
      })
    case 'rounded':
      return $t({
        defaultMessage: 'Square',
        id: 'Admin.Appearance.Style.Square',
      })
    case 'sharp':
      return $t({
        defaultMessage: 'Sharp',
        id: 'Admin.Appearance.Style.Sharp',
      })
    default: {
      const exhaustiveCheck: never = style
      return exhaustiveCheck
    }
  }
}

export const getTranslatedAvatarStyles = (
  $t: TranslateFunctionType,
  style: AppearanceStyle['avatars'],
) => {
  switch (style) {
    case 'cute':
      return $t({
        defaultMessage: 'Cute',
        id: 'Admin.Appearance.Style.Cute',
      })
    case 'neutral':
      return $t({
        defaultMessage: 'Neutral',
        id: 'Admin.Appearance.Style.Neutral',
      })
    case 'initials':
      return $t({
        defaultMessage: 'Initials',
        id: 'Admin.Appearance.Style.Initials',
      })
    default: {
      const exhaustiveCheck: never = style
      return exhaustiveCheck
    }
  }
}

export const getTranslatedIconStyles = (
  $t: TranslateFunctionType,
  style: AppearanceStyle['icons'],
) => {
  switch (style) {
    case 'line':
      return $t({
        defaultMessage: 'Line',
        id: 'Admin.Appearance.Style.Line',
      })
    case 'solid':
      return $t({
        defaultMessage: 'Solid',
        id: 'Admin.Appearance.Style.Solid',
      })
    case 'duocolor':
      return $t({
        defaultMessage: 'Duocolor',
        id: 'Admin.Appearance.Style.Duocolor',
      })
    default: {
      const exhaustiveCheck: never = style
      return exhaustiveCheck
    }
  }
}

export const getTranslatedColorScheme = (
  $t: TranslateFunctionType,
  colorScheme: ColorScheme,
) => {
  switch (colorScheme) {
    case 'light1':
      return $t({
        defaultMessage: 'Light 1',
        id: 'Admin.Appearance.ThemeMode.Light1',
      })
    case 'light2':
      return $t({
        defaultMessage: 'Light 2',
        id: 'Admin.Appearance.ThemeMode.Light2',
      })
    case 'light3':
      return $t({
        defaultMessage: 'Light 3',
        id: 'Admin.Appearance.ThemeMode.Light3',
      })
    case 'dark1':
      return $t({
        defaultMessage: 'Dark 1',
        id: 'Admin.Appearance.ThemeMode.Dark1',
      })
    case 'dark2':
      return $t({
        defaultMessage: 'Dark 2',
        id: 'Admin.Appearance.ThemeMode.Dark2',
      })
    default: {
      const exhaustiveCheck: never = colorScheme
      return exhaustiveCheck
    }
  }
}

export const getTranslatedNetworkColorSchemeMode = (
  $t: TranslateFunctionType,
  mode: NetworkColorScheme['mode'],
) => {
  switch (mode) {
    case 'single':
      return $t({
        defaultMessage: 'Single theme',
        id: 'Admin.Appearance.ThemeMode.SingleTheme',
      })
    case 'user-preference':
      return $t({
        defaultMessage: 'Offer dual themes',
        id: 'Admin.Appearance.ThemeMode.DualThemes',
      })
    default: {
      const exhaustiveCheck: never = mode
      return exhaustiveCheck
    }
  }
}

export const getTranslatedNetworkColorSchemeModeDescription = (
  $t: TranslateFunctionType,
  mode: NetworkColorScheme['mode'],
) => {
  switch (mode) {
    case 'single':
      return $t({
        defaultMessage:
          'Choose a specific theme for the interface, either light or dark, disregarding the user`s system settings.',
        id: 'Admin.Appearance.ThemeMode.SingleTheme.Description',
      })
    case 'user-preference':
      return $t({
        defaultMessage:
          'Select to provide users with the choice between light and dark themes, or sync with their system settings',
        id: 'Admin.Appearance.ThemeMode.DualThemes.Description',
      })
    default: {
      const exhaustiveCheck: never = mode
      return exhaustiveCheck
    }
  }
}
